/**
 * OAuth Popup Service
 *
 * Single responsibility: open provider popup and return auth result via postMessage.
 */

import { getApiBaseUrl } from '../config/api';
import { getAppOrigin } from '../config/google';
import { OAUTH_CANCELLED, OAUTH_POPUP_BLOCKED, OAUTH_POPUP_TIMEOUT } from '../utils/oauthErrors';

export { OAUTH_CANCELLED, OAUTH_POPUP_BLOCKED, OAUTH_POPUP_TIMEOUT };

export interface OAuthUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  photo?: string;
}

export interface OAuthResult {
  success: boolean;
  requiresTwoFactor?: boolean;
  twoFactorToken?: string;
  accessToken?: string;
  refreshToken?: string;
  user?: OAuthUser;
  googleAccountLinked?: boolean;
  isNewUser?: boolean;
  error?: string;
}

class OAuthPopupService {
  private readonly POPUP_WIDTH = 500;
  private readonly POPUP_HEIGHT = 600;
  private readonly POPUP_TIMEOUT = 5 * 60 * 1000;

  async loginWithGoogle(): Promise<OAuthResult> {
    const oauthUrl = `${getApiBaseUrl()}/auth/google/popup`;
    return this.openOAuthPopup(oauthUrl, 'Google Sign In');
  }

  /** Link Google to the currently signed-in account (call prepareGoogleLink first). */
  async linkGoogleAccount(): Promise<OAuthResult> {
    const oauthUrl = `${getApiBaseUrl()}/auth/google/popup`;
    return this.openOAuthPopup(oauthUrl, 'Connect Google');
  }

  private getAllowedOrigins(): string[] {
    const origins = new Set<string>([window.location.origin, getAppOrigin()]);

    try {
      origins.add(new URL(getApiBaseUrl()).origin);
    } catch {
      // ignore invalid API URL
    }

    if (import.meta.env.DEV) {
      origins.add('http://localhost:5173');
      origins.add('http://127.0.0.1:5173');
      origins.add('http://localhost:4000');
    }

    const configured = import.meta.env.VITE_OAUTH_ALLOWED_ORIGINS;
    if (typeof configured === 'string') {
      configured
        .split(',')
        .map((origin) => origin.trim())
        .filter(Boolean)
        .forEach((origin) => origins.add(origin));
    }

    return [...origins];
  }

  private async openOAuthPopup(url: string, title: string): Promise<OAuthResult> {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (result: OAuthResult) => {
        if (settled) return;
        settled = true;
        resolve(result);
      };

      const left = (screen.width - this.POPUP_WIDTH) / 2;
      const top = (screen.height - this.POPUP_HEIGHT) / 2;

      const popup = window.open(
        url,
        title,
        `width=${this.POPUP_WIDTH},height=${this.POPUP_HEIGHT},left=${left},top=${top},toolbar=no,menubar=no`,
      );

      if (!popup) {
        finish({
          success: false,
          error: OAUTH_POPUP_BLOCKED,
        });
        return;
      }

      const allowedOrigins = this.getAllowedOrigins();

      const cleanup = () => {
        clearTimeout(timeout);
        clearInterval(pollTimer);
        window.removeEventListener('message', messageHandler);
      };

      const timeout = setTimeout(() => {
        if (!popup.closed) popup.close();
        cleanup();
        finish({
          success: false,
          error: OAUTH_POPUP_TIMEOUT,
        });
      }, this.POPUP_TIMEOUT);

      const messageHandler = (event: MessageEvent) => {
        if (!allowedOrigins.includes(event.origin)) return;

        if (event.data?.type === 'oauth-success') {
          cleanup();
          if (!popup.closed) popup.close();
          finish({
            success: true,
            accessToken: event.data.accessToken,
            refreshToken: event.data.refreshToken,
            user: event.data.user,
            googleAccountLinked: event.data.googleAccountLinked === true,
            isNewUser: event.data.isNewUser === true,
          });
          return;
        }

        if (event.data?.type === 'oauth-2fa-required') {
          cleanup();
          if (!popup.closed) popup.close();
          finish({
            success: true,
            requiresTwoFactor: true,
            twoFactorToken: event.data.twoFactorToken,
            user: event.data.user,
          });
          return;
        }

        if (event.data?.type === 'oauth-link-success') {
          cleanup();
          if (!popup.closed) popup.close();
          finish({ success: true });
          return;
        }

        if (event.data?.type === 'oauth-error') {
          cleanup();
          if (!popup.closed) popup.close();
          finish({
            success: false,
            error: event.data.error || 'Authentication failed',
          });
        }
      };

      window.addEventListener('message', messageHandler);

      const pollTimer = setInterval(() => {
        if (popup.closed) {
          cleanup();
          finish({
            success: false,
            error: OAUTH_CANCELLED,
          });
        }
      }, 500);
    });
  }
}

export const oauthPopupService = new OAuthPopupService();
